/* This example requires Tailwind CSS v2.0+ */

import { ChartSquareBarIcon, UserGroupIcon } from '@heroicons/react/outline';
import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';

const options = [
  {
    name: 'Choose Your Date',
    type: 'signup',
    description: 'Select your date, and book your seat.',
  },
  {
    name: 'Join the Workshop',
    type: 'share',
    description:
      'Grab your laptop, and get ready for an unforgettable experience!',
  },
  {
    name: 'Hands-on Practice',
    type: 'earn',
    description:
      'Enjoy 3 hours of quick, easy, and hassle-free hands-on practice.',
  },
];

const features = [
  {
    name: 'Unlocking Full Potential:',
    description:
      "During the workshop, you’ll get hands-on access to the Unlimited plan of Easy-Peasy.AI, including our main modules – and learn exactly how to apply them in your day-to-day work.",
    icon: ChartSquareBarIcon, // Analytics/business/insight
  },
  {
    name: 'Hands-on Experience:',
    description:
      "You'll get to engage directly with the Easy-Peasy.AI platform, experiencing first-hand how generative AI can be applied to various aspects of your business.",
    icon: require('@heroicons/react/outline').LightningBoltIcon, // Action/energy/hands-on
  },
  {
    name: 'Questions about your business case:',
    description:
      'We will share how innovative AI-driven solutions can be tailored to your business needs.',
    icon: require('@heroicons/react/outline').AnnotationIcon, // Discussion/answers/consultation
  },
  {
    name: 'Content Creation Enhancement:',
    description:
      'Improve content creation 10x faster with 300+ tools and in 40+ languages.',
    icon: require('@heroicons/react/outline').PencilAltIcon, // Writing/content creation
  },
  {
    name: 'Customer Support Automation:',
    description:
      'Learn to create AI bots for streamlined operations and improved customer engagement.',
    icon: require('@heroicons/react/outline').SupportIcon, // Support/customer service
  },
  {
    name: 'Ongoing Support and Networking:',
    description:
      "You'll receive the complete workshop slides and the guide. You will be connected with our community of innovative business owners, ensuring ongoing support.",
    icon: require('@heroicons/react/outline').UserGroupIcon, // Community/networking
  },
];

export default function TranscriptionFeaturesAffil() {
  const { t } = useTranslation('common');

  return (
    <section>
      <div className='bg-white pb-12 pt-32 dark:bg-slate-900'>
        <div className='mx-auto max-w-xl px-4 text-center sm:px-6 lg:max-w-7xl lg:px-8'>
          <h2 className='mx-auto mt-2 max-w-3xl text-center text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            {t('How To Walk Out As An Expert?')}
          </h2>
          <p className='mx-auto mt-5 max-w-prose text-xl text-gray-500 dark:text-slate-400'>
            {t(
              'Three easy steps to walk out as an expert with our proven tools and strategies for success!'
            )}
          </p>
          <dl className='mt-12 space-y-10 text-center lg:grid lg:grid-cols-3 lg:gap-8 lg:space-y-0'>
            {options.map((feature) => (
              <div key={feature.name}>
                <dt className='text-center'>
                  <div className='mx-auto flex h-12 w-12 items-center justify-center rounded-md bg-indigo-500 text-white'>
                    <span className='text-lg font-bold'>
                      {options.indexOf(feature) + 1}
                    </span>
                  </div>
                  <strong className='mt-5 block text-lg font-medium leading-6 text-gray-900 dark:text-slate-300'>
                    {t(feature.name)}
                  </strong>
                </dt>
                <dd className='mt-2 text-base text-gray-500'>
                  {t(feature.description)}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 lg:pt-24 lg:pb-12">
        <div className="mx-auto max-w-3xl px-6 lg:px-8 flex flex-col items-center">
          {/* Image at the top */}
          <div className="w-full flex justify-center items-center mb-12">
            <div className="relative aspect-video w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden">
              <Image
                src="/images/about-us/Workshop47.jpeg"
                alt="AI Workshop Introduction"
                width={1200}
                height={675}
                className="rounded-2xl object-cover w-full h-full"
              />
            </div>
          </div>
          {/* Heading and Description */}
          <h3 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl text-center">
            {t('What To Expect In Our Workshop')}
          </h3>
          <p className="mt-4 text-lg text-gray-500 dark:text-slate-400 text-center">
            {t(
              "We will start by gaining a foundational understanding of generative AI, how it differs from other types of AI, and why it's particularly relevant and transformative for businesses today."
            )}
          </p>
          {/* Features List - Two Columns */}
          <div className="mt-12 w-full">
            <dl className="grid grid-cols-1 gap-10 md:grid-cols-2">
              {features.map((feature) => (
                <div key={feature.name} className="relative flex">
                  <feature.icon
                    className="h-6 w-6 text-primary-500 flex-shrink-0 mt-1"
                    aria-hidden="true"
                  />
                  <div className="ml-3">
                    <dt>
                      <p className="text-lg font-medium leading-6 text-gray-900 dark:text-slate-200">
                        {t(feature.name)}
                      </p>
                    </dt>
                    <dd className="mt-2 text-base text-gray-500 dark:text-slate-400">
                      {t(feature.description)}
                    </dd>
                  </div>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>
    </section>
  );
}
